import React, { useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { useJsApiLoader } from "@react-google-maps/api";
import useAxiosInstance from "../../lib/useAxiosInstance";
import ElectionModeMapView from "./ElectionModeMapView";
import ElectionModeNavbar from "./ElectionModeNavbar";
import ElectionModeSidebar from "./ElectionModeSidebar";
import ElectionModeLoader from "./ElectionModeLoader";
import "./ElectionModeClient.css";

const normalizeElectionModeId = (value) => {
  if (Array.isArray(value)) return value[0];
  if (value && typeof value === "object") return value._id || value.id;
  return value;
};

const buildPolygonPaths = (shapeList = []) => {
  const allPolygons = [];

  shapeList.forEach((shape) => {
    const { boundaries, name } = shape || {};
    if (!boundaries?.type || !boundaries?.coordinates) return;

    if (boundaries.type === "MultiPolygon") {
      boundaries.coordinates.forEach((poly) => {
        const ring = poly[0] || [];
        const coordinates = ring
          .map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }))
          .filter((point) => !Number.isNaN(point.lat) && !Number.isNaN(point.lng));

        if (coordinates.length) {
          allPolygons.push({ name, coordinate: coordinates });
        }
      });
    }

    if (boundaries.type === "Polygon") {
      boundaries.coordinates.forEach((ring) => {
        const coordinates = ring
          .map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }))
          .filter((point) => !Number.isNaN(point.lat) && !Number.isNaN(point.lng));

        if (coordinates.length) {
          allPolygons.push({ name, coordinate: coordinates });
        }
      });
    }
  });

  return allPolygons;
};

const ElectionModeFullScreen = ({ user }) => {
  const axiosInstance = useAxiosInstance();
  const mapRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  const [electionMode, setElectionMode] = useState(null);
  const [polygonPaths, setPolygonPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    region: "",
    district: "",
    constituency: "",
    party: "BJP",
  });
  // Populated via onFilterOptionsReady from ElectionModeDashboard
  const [filterData, setFilterData] = useState({ data: null, parties: [] });

  // Keep the boot loader on screen for a minimum duration so the animation is
  // always experienced, even when data loads almost instantly.
  const [minLoaderDone, setMinLoaderDone] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setMinLoaderDone(true), 10000);
    return () => clearTimeout(timer);
  }, []);

  const fallbackUser = user || JSON.parse(localStorage.getItem("user"));
  const electionModeId = normalizeElectionModeId(
    localStorage.getItem("electionModeId") || fallbackUser?.electionModeId
  );

  const { isLoaded, loadError } = useJsApiLoader({
    id: "map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  useEffect(() => {
    if (!electionModeId) {
      setLoading(false);
      return;
    }

    const fetchElectionMode = async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(`api/electionMode/${electionModeId}`);
        const electionData = res.data?.data;
        setElectionMode(electionData);

        if (electionData?.isShapeExist) {
          const shapeRes = await axiosInstance.get(
            `/api/electionMode/getShapeFile/${electionModeId}`
          );
          setPolygonPaths(buildPolygonPaths(shapeRes.data?.data || []));
        } else {
          setPolygonPaths([]);
        }
      } catch (error) {
        console.error(error);
        message.error("Failed to load election mode dashboard");
      } finally {
        setLoading(false);
      }
    };

    fetchElectionMode();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [electionModeId]);
  const defaultCenter = useMemo(() => {
    if (polygonPaths[0]?.coordinate?.length) {
      return polygonPaths[0].coordinate[0];
    }
    return { lat: 26.8467, lng: 80.9462 };
  }, [polygonPaths]);

  if (loadError) {
    return (
      <div className="election-mode-client-shell flex items-center justify-center text-white">
        Failed to load map
      </div>
    );
  }

  if (loading || !isLoaded) {
    return (
      <ElectionModeLoader
        electionModeId={electionModeId}
        title={electionMode?.electionModeName}
        logoUrl={electionMode?.logo?.publicUrl}
      />
    );
  }

  if (!electionModeId) {
    return (
      <div className="election-mode-client-shell flex items-center justify-center text-white">
        Election mode is enabled, but no election mode id was found.
      </div>
    );
  }

  return (
    <div className="election-mode-client-shell">
      <div className="election-mode-client-map">
        <ElectionModeMapView
          isLoaded={isLoaded}
          open
          setIsFullScreen={() => {}}
          id={electionModeId}
          center={defaultCenter}
          polygonPaths={polygonPaths}
          mapRef={mapRef}
          electionMode={electionMode}
          showCloseButton={false}
          leftPanelOpen={leftPanelOpen}
          setLeftPanelOpen={setLeftPanelOpen}
          filters={filters}
          setFilters={setFilters}
          onFilterOptionsReady={(opts) => setFilterData(opts)}
        />
      </div>
      <div className="election-mode-navbar">
        <ElectionModeNavbar
          title={electionMode?.electionModeName}
          logoUrl={electionMode?.logo?.publicUrl}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => {
            setSidebarOpen((prev) => {
              const next = !prev;
              setLeftPanelOpen(!next);
              return next;
            });
          }}
          filters={filters}
          setFilters={setFilters}
          filterData={filterData.data}
          parties={filterData.parties}
          electionModeType={electionMode?.electionModeType}
          urbanRural={electionMode?.client?.urbanRural}
        />
      </div>
      <ElectionModeSidebar open={sidebarOpen} role={fallbackUser?.role} />
    </div>
  );
};

export default ElectionModeFullScreen;
